import React, { useState } from 'react';
import { X, Upload, Image as ImageIcon, Palette, Ruler, User, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIAS = [
  { value: 'color', label: 'Referencia de Color', icon: Palette },
  { value: 'diseño', label: 'Diseño/Estilo', icon: ImageIcon },
  { value: 'medidas', label: 'Medidas', icon: Ruler },
  { value: 'cliente', label: 'Cliente', icon: User },
  { value: 'otros', label: 'Otros', icon: FileText }
];

export const PhotoGallery = ({ sheetId, photos = [], onPhotoAdded, onPhotoDeleted }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('color');
  const [descripcion, setDescripcion] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona un archivo de imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!previewImage) {
      toast.error('Selecciona una imagen primero');
      return;
    }

    try {
      setUploading(true);
      const response = await axios.post(`${API_URL}/sheets/${sheetId}/photos`, {
        data: previewImage,
        categoria: selectedCategory,
        descripcion: descripcion || null
      });
      
      toast.success('Foto agregada exitosamente');
      setPreviewImage(null);
      setDescripcion('');
      if (onPhotoAdded) onPhotoAdded(response.data);
    } catch (error) {
      toast.error('Error al subir la foto');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('¿Eliminar esta foto?')) return;

    try {
      await axios.delete(`${API_URL}/sheets/${sheetId}/photos/${photoId}`);
      toast.success('Foto eliminada');
      if (onPhotoDeleted) onPhotoDeleted(photoId);
    } catch (error) {
      toast.error('Error al eliminar la foto');
    }
  };

  const getCategoryIcon = (categoria) => {
    const cat = CATEGORIAS.find(c => c.value === categoria);
    if (cat) {
      const Icon = cat.icon;
      return <Icon className="w-4 h-4" />;
    }
    return <FileText className="w-4 h-4" />;
  };

  const getCategoryLabel = (categoria) => {
    const cat = CATEGORIAS.find(c => c.value === categoria);
    return cat ? cat.label : categoria;
  };

  return (
    <div>
      <div className="bg-zinc-50 border border-border p-6 mb-6">
        <label className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3 block">
          Subir Nueva Foto
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs text-muted-foreground mb-2 block">Categoría *</label>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {CATEGORIAS.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setSelectedCategory(cat.value)}
                    className={`flex items-center gap-2 p-3 border rounded-none text-xs transition-all ${
                      selectedCategory === cat.value
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-black border-border hover:border-black'
                    }`}
                    data-testid={`category-${cat.value}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{cat.label}</span>
                  </button>
                );
              })}
            </div>

            <label className="text-xs text-muted-foreground mb-2 block">Descripción (opcional)</label>
            <input
              type="text"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Ej: Tela italiana azul marino"
              className="w-full h-10 border border-input bg-white px-3 text-sm focus:outline-none focus:border-black transition-colors"
              data-testid="photo-description"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground mb-2 block">Imagen *</label>
            <div className="border-2 border-dashed border-border bg-white p-4 text-center">
              {previewImage ? (
                <div className="relative">
                  <img src={previewImage} alt="Preview" className="max-h-48 mx-auto" />
                  <button
                    type="button"
                    onClick={() => setPreviewImage(null)}
                    className="absolute top-2 right-2 bg-black text-white p-1 rounded-full"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="photo-input"
                  />
                  <div className="py-8">
                    <Upload className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-1">Click para seleccionar</p>
                    <p className="text-xs text-muted-foreground">PNG, JPG hasta 5MB</p>
                  </div>
                </label>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleUpload}
          disabled={!previewImage || uploading}
          className="mt-4 bg-black text-white hover:bg-zinc-800 rounded-none h-10 px-6 uppercase tracking-widest text-xs font-medium transition-all disabled:opacity-50"
          data-testid="upload-photo-button"
        >
          {uploading ? 'Subiendo...' : 'Subir Foto'}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.length === 0 ? (
          <div className="col-span-full text-center py-8 text-sm text-muted-foreground">
            No hay fotos aún. Sube la primera referencia de color o diseño.
          </div>
        ) : (
          photos.map((photo) => (
            <div
              key={photo.id}
              className="group relative bg-white border border-border overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setSelectedPhoto(photo)}
              data-testid={`photo-${photo.id}`}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={photo.data}
                  alt={photo.descripcion || 'Foto'}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-2">
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                  {getCategoryIcon(photo.categoria)}
                  <span>{getCategoryLabel(photo.categoria)}</span>
                </div>
                {photo.descripcion && (
                  <p className="text-xs text-black truncate">{photo.descripcion}</p>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(photo.id);
                }}
                className="absolute top-2 right-2 bg-black bg-opacity-70 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-photo-${photo.id}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>

      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={selectedPhoto.data}
              alt={selectedPhoto.descripcion}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-4">
              <div className="flex items-center gap-2 mb-1">
                {getCategoryIcon(selectedPhoto.categoria)}
                <span className="text-sm uppercase tracking-wider">
                  {getCategoryLabel(selectedPhoto.categoria)}
                </span>
              </div>
              {selectedPhoto.descripcion && (
                <p className="text-sm">{selectedPhoto.descripcion}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};